import moment from "moment";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Buffer } from 'buffer';

interface GroupByFuncProps<T> {
    (item: T): any
}

export const formatMoney = (value) => {
  return parseFloat(value)
    .toLocaleString("pt-BR", {
      currency: 'BRL',
      style: 'currency',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })
}

export const groupBy = <T>(
    list: T[],
    func: GroupByFuncProps<T>,
    keyValidator?: string,
) : [any, T[]][] => {
    const map: any[] = [];
    list.forEach((item) => {
        const key = func(item);
        let i = -1;
        map.forEach((mapItem, index) => {
            if (i === -1 && (
            (mapItem[0] === key)
            || (keyValidator && mapItem[0][keyValidator] === key[keyValidator])
            )) i = index;
        });
        if (i === -1) map.push([key, [item]]);
        else map[i][1].push(item);
    });
    return map;
};

export function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const extenseWeekday = (weekday) => [
    "Domingo",
    "Segunda-feira",
    "Terça-feira",
    "Quarta-feira",
    "Quinta-feira",
    "Sexta-feira",
    "Sábado",
][weekday];

export const months = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
]

export const spentTime = dt => {
    const months = moment().diff(moment(dt), "months")
    const ds = moment().diff(moment(dt), "days")
    const hours = moment().diff(moment(dt), "hours")
    const minutes = moment().diff(moment(dt), "minutes")
    return months > 0
        ? `${months} meses atrás`
        : ds > 0
            ? `${ds} dias atrás`
            : hours > 0 ? `${hours} horas atrás` : `${minutes} minutos atrás`
}

export function useOnScreen(ref) {

    const [isIntersecting, setIntersecting] = React.useState(false)
  
    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting)
    )
  
    React.useEffect(() => {
      observer.observe(ref.current)
      // Remove the observer as soon as the component is unmounted
      return () => { observer.disconnect() }
    }, [])
  
    return isIntersecting
  }


export const getEntityProfile = item => ({
    "senador": "Senador",
    "deputado_federal": "Deputado Federal",
    "deputado_distrital": "Deputado Distrital",
    "orgao": "Órgão",
}[item.type] || item.type);

export const parseKeyword = (key) => key ? key
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^\w\s]/gi, '')
  .toLowerCase() : "";

export const useAudio = url => {
    const [audio] = useState(new Audio(url));
    const [playing, setPlaying] = useState(false);
    const [interacted, setInteracted] = useState(false);
  
    const toggle = () => setPlaying(!playing);
    const play = () => setPlaying(true);
    const pause = () => setPlaying(false);

    const handleInteraction = () => setInteracted(true);

    const setVolume = volume => {
      audio.volume = volume;
    }
  
    useEffect(() => {
      if(interacted){
        audio.currentTime = 0;
        audio.loop = false;
        playing ? audio.play() : audio.pause();
      }
      }, [playing, interacted]
    );
  
    useEffect(() => {
      audio.addEventListener('ended', () => setPlaying(false));
      return () => {
        audio.removeEventListener('ended', () => setPlaying(false));
      };
    }, []);

    useEffect(() => {
      document.addEventListener('click', handleInteraction);
      return () => { document.removeEventListener('click', handleInteraction); };
    }, [])
  
    return {playing, play, pause, toggle, setVolume};
};

export const formatMilliseconds = (milliseconds) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
  const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  return parseInt((totalSeconds / 3600).toFixed(0)) > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
};

export const getExtenseDatetime = (datetime, hours = true) => {
  const extenseDate = moment(datetime).format("YYYY-MM-DD") === moment().subtract(1, "days").format("YYYY-MM-DD")
    ? "Ontem"
    : moment(datetime).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")
      ? "Hoje"
      : moment(datetime).format("DD/MM/YYYY")
  return `${extenseDate}${hours ? `, ${moment(datetime).format("HH:mm")}` : ""}`;
}


export const getDeviceTitle = item => {
  if(!item) return "";
  if(item.origin === "getrak") return `Getrak ${item.props?.id_veiculo}`
  // if(item.origin === "st300") return `ST300 ${item.props?.DEV_ID}`
  // if(item.origin === "st4315") return `ST4315 ${item.props?.DEV_ID}`
  // if(item.origin === "nt20") return `NT20 ${item.props?.DEV_ID}`
  return `${(item?.model?.title ?? item?.origin ?? "").toUpperCase()} ${item.props?.DEV_ID}`;
}

export function generateMarkerText(number) {
  number = number + 1;
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let result = '';

  while (number > 0) {
    const remainder = (number - 1) % 26;
    result = alphabet[remainder] + result;
    number = Math.floor((number - 1) / 26);
  }

  return result || 'A';
}

export function createMarkerIcon(text, options : any = {}) {
  const wd : any = window;
  const google = wd.google;
  const size = options.size || new google.maps.Size(21, 34);
  const origin = options.origin || new google.maps.Point(0, 0);
  const anchor = options.anchor || new google.maps.Point(10, 34);
  return {
    url: `https://chart.googleapis.com/chart?chst=d_map_pin_letter&chld=${text ?? ""}|${options.color ?? "FF0000"}|FFFFFF`,
    size,
    origin,
    anchor,
  };
}

export const vehicleTypes = [
  { label: "Bicicleta", value: 'bike' },
  { label: "Caminhão", value: 'truck' },
  { label: "Carro", value: 'car' },
  { label: "Jetski", value: 'jetski' },
  { label: "Metrô / Trem", value: 'subway' },
  { label: "Motocicleta", value: 'moto' },
  { label: "Navio", value: 'ship' },
  { label: "Ônibus", value: 'bus' },
  { label: "Trator", value: 'tractor' },
  { label: "Van", value: 'van' },
]

export function getVehicleTitle(type){
  return vehicleTypes.find(vt => vt.value === type)?.label
}

export function getVehicleIcon(type){
  return {
    'bike': '/icons/bicicleta.png',
    'truck': '/icons/caminhao.png',
    'car': '/icons/carro.png',
    'jetski': '/icons/jetski.png',
    'subway': '/icons/metro.png',
    'moto': '/icons/moto.png',
    'ship': '/icons/navio.png',
    'bus': '/icons/onibus.png',
    'tractor': '/icons/trator.png',
    'van': '/icons/van.png',
  }[type] || "/icons/carro.png"
}

export function formatDuration(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const hoursDisplay = hours > 0 ? hours + 'h ' : '';
  const minutesDisplay = minutes > 0 ? minutes + 'm ' : '';
  const secondsDisplay = remainingSeconds + 's';

  const formattedTime = hoursDisplay + minutesDisplay + secondsDisplay;
  return formattedTime.trim();
}

export const formatAddress = ({ address = null, location = null } = {}) => [
  address?.address?.road,
  address?.address?.town,
  // address?.address?.municipality,
  address?.address?.city,
  address?.address?.city_district,
  address?.address?.state,
  // location?.lat ?? location?.latitude,
  // location?.lng ?? location?.longitude,
].filter(nn => nn).join(", ")

export const vehiclePath = "M 124.661 106.750 L 124.203 107.500 123.156 115 L 122.109 122.500 121.452 132.365 L 120.796 142.231 115.648 141.495 L 110.500 140.759 100.581 140.119 L 90.662 139.479 86.831 139.845 L 83 140.210 83 152.267 L 83 164.324 80.514 168.566 L 78.028 172.808 73.698 174.904 L 69.369 177 53.500 177 L 37.631 177 33.321 174.913 L 29.011 172.827 27.290 169.913 L 25.569 167 24.936 167 L 24.304 167 22.652 172.115 L 21 177.230 21 180.080 L 21 182.931 17.997 187.666 L 14.993 192.402 13.007 197.711 L 11.021 203.019 8.887 214.259 L 6.753 225.500 6.260 248.732 L 5.768 271.963 6.981 283.732 L 8.193 295.500 10.567 304.861 L 12.941 314.222 16.971 321.564 L 21 328.906 21 331.775 L 21 334.644 22.594 339.322 L 24.188 344 24.476 344 L 24.765 344 28.669 340.096 L 32.573 336.192 36.970 335.008 L 41.368 333.824 56.010 334.162 L 70.652 334.500 74.806 337.251 L 78.960 340.002 80.980 343.961 L 83 347.920 83 359.960 L 83 372 91.250 371.978 L 99.500 371.957 110 370.567 L 120.500 369.177 120.789 369.460 L 121.078 369.744 121.981 383.122 L 122.884 396.500 123.573 400.772 L 124.263 405.044 125.190 405.617 L 126.117 406.191 130.063 403.456 L 134.009 400.722 135.876 397.611 L 137.743 394.500 138.400 391.712 L 139.057 388.923 137.473 385.584 L 135.888 382.245 131.944 379.075 L 128 375.906 128 371.453 L 128 367 128.750 366.998 L 129.500 366.996 144 365.554 L 158.500 364.112 189.500 362.057 L 220.500 360.002 248.500 358.484 L 276.500 356.966 297.737 356.324 L 318.973 355.682 320.276 351.091 L 321.579 346.500 315.539 345.923 L 309.500 345.345 315.315 345.173 L 321.129 345 323.768 341.864 L 326.407 338.728 329.564 337.409 L 332.721 336.090 351.611 337.012 L 370.500 337.934 389.500 337.957 L 408.500 337.981 425.500 336.961 L 442.500 335.941 453 334.427 L 463.500 332.912 470.843 330.934 L 478.186 328.955 481.343 327.361 L 484.500 325.766 486.746 323.712 L 488.992 321.659 489.746 322.427 L 490.500 323.195 487.130 326.020 L 483.760 328.846 478.130 330.937 L 472.500 333.029 465.294 334.519 L 458.089 336.008 445.294 337.569 L 432.500 339.129 402 339.644 L 371.500 340.158 352.581 339.250 L 333.663 338.342 331.356 338.783 L 329.049 339.224 326.527 341.746 L 324.005 344.268 322.943 348.384 L 321.881 352.500 321.373 354.250 L 320.865 356 325.381 356 L 329.898 356 341.699 357.535 L 353.500 359.069 361.500 360.957 L 369.500 362.845 377 365.339 L 384.500 367.832 392.500 369.103 L 400.500 370.373 419 370.327 L 437.500 370.282 446 369.094 L 454.500 367.906 464.591 365.511 L 474.683 363.115 480.540 360.525 L 486.397 357.934 489.708 355.409 L 493.018 352.884 495.020 348.961 L 497.021 345.038 499.003 336.769 L 500.986 328.500 502.479 317.500 L 503.971 306.500 504.976 292 L 505.980 277.500 505.974 245 L 505.968 212.500 504.996 200.765 L 504.023 189.029 502.468 181.265 L 500.913 173.500 497.908 167.278 L 494.902 161.056 491.701 157.850 L 488.500 154.645 481.500 151.430 L 474.500 148.215 466 146.153 L 457.500 144.091 449 142.796 L 440.500 141.500 418 141.513 L 395.500 141.526 389.500 142.827 L 383.500 144.128 376 146.543 L 368.500 148.957 360 150.915 L 351.500 152.873 342 153.909 L 332.500 154.946 326.856 155.531 L 321.212 156.117 322.462 160.855 L 323.711 165.592 324.885 167.786 L 326.059 169.979 329.085 171.544 L 332.111 173.109 362.805 172.075 L 393.500 171.042 416 171.987 L 438.500 172.932 449.500 174.385 L 460.500 175.838 467.050 177.358 L 473.600 178.879 479.050 180.932 L 484.500 182.985 487.500 185.403 L 490.500 187.821 489.756 188.571 L 489.012 189.321 487.256 187.777 L 485.500 186.233 481.256 184.099 L 477.012 181.965 468.756 180.017 L 460.500 178.069 448.500 176.313 L 436.500 174.556 382.920 174.504 L 329.339 174.452 326.624 172.673 L 323.909 170.894 322.808 168.478 L 321.708 166.063 320.865 166.583 L 320.023 167.104 320.578 165.657 L 321.134 164.210 320.071 160.264 L 319.008 156.317 298.254 155.650 L 277.500 154.983 249.500 153.483 L 221.500 151.983 190.500 149.936 L 159.500 147.888 145 146.446 L 130.500 145.004 129.250 145.002 L 128 145 128 140.707 L 128 136.413 133 131.694 L 138 126.975 138 122.356 L 138 117.736 136.004 114.507 L 134.009 111.278 130.201 108.639 L 126.392 106 125.755 106 L 125.118 106 124.661 106.750 M 73 140.680 L 69.500 141.040 62 142.639 L 54.500 144.237 49.636 146.149 L 44.772 148.061 40.136 151.175 L 35.500 154.290 32.250 157.341 L 29 160.392 29 162.314 L 29 164.235 32.949 168.184 L 36.898 172.133 53.932 171.816 L 70.967 171.500 74.233 168.234 L 77.500 164.967 77.832 152.484 L 78.163 140 77.332 140.160 L 76.500 140.320 73 140.680 M 227.250 160.689 L 229 161.147 230.750 160.689 L 232.500 160.232 229 160.232 L 225.500 160.232 227.250 160.689 M 241.250 161.689 L 243 162.147 244.750 161.689 L 246.500 161.232 243 161.232 L 239.500 161.232 241.250 161.689 M 255.250 162.689 L 257 163.147 258.750 162.689 L 260.500 162.232 257 162.232 L 253.500 162.232 255.250 162.689 M 270.762 163.707 L 273.023 164.142 274.762 163.687 L 276.500 163.232 272.500 163.252 L 268.500 163.272 270.762 163.707 M 286.762 164.707 L 289.023 165.142 290.762 164.687 L 292.500 164.232 288.500 164.252 L 284.500 164.272 286.762 164.707 M 303.750 165.706 L 306 166.139 308.250 165.706 L 310.500 165.272 306 165.272 L 301.500 165.272 303.750 165.706 M 217 167.276 L 217 167.864 234.895 182.803 L 252.790 197.742 285.145 198.403 L 317.500 199.065 323.657 199.639 L 329.814 200.213 331.487 197.660 L 333.160 195.108 332.330 193.649 L 331.500 192.190 322.683 181.787 L 313.867 171.384 282.683 169.650 L 251.500 167.916 234.250 167.302 L 217 166.688 217 167.276 M 195.308 173.010 L 193.117 173.892 191.640 176.196 L 190.163 178.500 188.985 182.963 L 187.808 187.427 186.358 198.963 L 184.908 210.500 184.327 244 L 183.745 277.500 184.913 294.701 L 186.081 311.901 187.556 321.020 L 189.031 330.138 190.479 333.605 L 191.928 337.072 195.714 338.663 L 199.500 340.254 203.186 338.245 L 206.871 336.237 226.436 321.920 L 246 307.603 246 256 L 246 204.397 226.750 190.324 L 207.500 176.251 204 174.129 L 200.500 172.007 199 172.067 L 197.500 172.127 195.308 173.010 M 233.595 174.710 L 236.690 177.500 245.675 185.101 L 254.661 192.702 290.495 193.437 L 326.330 194.172 322.220 189.336 L 318.110 184.500 314.305 180.339 L 310.500 176.178 278 174.575 L 245.500 172.972 238 172.446 L 230.500 171.920 233.595 174.710 M 195.637 180.250 L 194.227 182.500 193.101 189 L 191.974 195.500 190.933 204.500 L 189.892 213.500 189.311 243.500 L 188.730 273.500 189.879 291.513 L 191.028 309.525 192.467 319.112 L 193.906 328.700 195.114 331.350 L 196.321 334 198.728 334 L 201.135 334 219.969 320.313 L 238.804 306.626 239.537 290.063 L 240.270 273.500 239.779 240.167 L 239.288 206.834 236.894 204.576 L 234.500 202.319 217.779 190.159 L 201.058 178 199.052 178 L 197.046 178 195.637 180.250 M 362 181.559 L 360.500 181.885 350.735 182.537 L 340.969 183.189 340.094 184.844 L 339.219 186.500 340.598 193 L 341.978 199.500 343.065 227.750 L 344.152 256 343.065 284.250 L 341.978 312.500 340.610 318.984 L 339.242 325.468 339.732 326.743 L 340.221 328.017 342.360 328.517 L 344.500 329.017 356 330 L 367.500 330.983 390 330.961 L 412.500 330.938 419.983 329.967 L 427.465 328.996 430.240 326.222 L 433.014 323.447 434.432 316.474 L 435.849 309.500 436.925 297.328 L 438 285.156 438 256 L 438 226.844 436.925 214.672 L 435.849 202.500 434.432 195.526 L 433.014 188.553 430.240 185.778 L 427.465 183.004 419.983 182.033 L 412.500 181.062 388 181.147 L 363.500 181.232 362 181.559 M 30.667 193.750 L 29.045 201.500 27.571 210.500 L 26.096 219.500 25.048 229.211 L 24 238.921 24 256 L 24 273.079 25.048 282.789 L 26.096 292.500 27.571 301.500 L 29.045 310.500 30.667 318.250 L 32.289 326 33.645 326 L 35 326 35 325.062 L 35 324.124 33.419 317.312 L 31.837 310.500 29.854 297.500 L 27.871 284.500 27.296 264.389 L 26.721 244.277 27.919 231.526 L 29.118 218.775 31.580 205.138 L 34.042 191.500 34.637 188.750 L 35.233 186 33.761 186 L 32.289 186 30.667 193.750 M 363 186.595 L 359.500 186.988 352.373 187.568 L 345.246 188.148 346.078 191.852 L 346.910 195.556 347.955 208.423 L 349 221.290 349 256 L 349 290.710 347.955 303.577 L 346.910 316.444 346.078 320.148 L 345.246 323.852 352.873 324.676 L 360.500 325.500 391 325.500 L 421.500 325.500 424.500 323.995 L 427.500 322.490 429.163 315.840 L 430.826 309.190 432.038 295.845 L 433.250 282.500 433.250 256 L 433.250 229.500 432.038 216.155 L 430.826 202.810 429.163 196.160 L 427.500 189.510 424.500 188.058 L 421.500 186.607 394 186.404 L 366.500 186.202 363 186.595 M 467.298 188.104 L 467.887 190.500 469.191 201.500 L 470.496 212.500 470.496 256 L 470.496 299.500 469.191 310.500 L 467.887 321.500 467.298 323.896 L 466.710 326.293 471.513 325.751 L 476.317 325.210 483.828 321.355 L 491.339 317.500 493.377 314.734 L 495.414 311.969 496.131 300.234 L 496.848 288.500 496.848 256 L 496.848 223.500 496.131 211.766 L 495.414 200.031 493.384 197.266 L 491.355 194.500 483.627 190.621 L 475.899 186.743 471.304 186.225 L 466.710 185.707 467.298 188.104 M 123.894 196.056 L 82.289 202.799 79.319 204.335 L 76.350 205.871 72.816 209.782 L 69.283 213.693 67.391 218.596 L 65.500 223.500 65.500 256 L 65.500 288.500 67.406 293.441 L 69.312 298.383 73.181 302.580 L 77.050 306.778 79.945 307.977 L 82.839 309.176 124.170 315.926 L 165.500 322.677 166.673 322.261 L 167.845 321.845 168.222 320.673 L 168.600 319.500 167.873 291 L 167.145 262.500 167.595 233.500 L 168.044 204.500 168.526 197.787 L 169.007 191.074 167.254 190.194 L 165.500 189.313 123.894 196.056 M 474.363 195.750 L 474.988 199.500 476.118 216.500 L 477.248 233.500 476.665 268.500 L 476.082 303.500 474.998 311.373 L 473.913 319.247 474.383 319.716 L 474.853 320.186 482.016 316.422 L 489.180 312.658 490.297 310.079 L 491.415 307.500 491.425 256 L 491.434 204.500 490.307 201.921 L 489.180 199.342 482.194 195.671 L 475.208 192 474.473 192 L 473.738 192 474.363 195.750 M 123.134 201.487 L 84.769 207.740 81.412 209.341 L 78.055 210.941 75.099 215.021 L 72.144 219.101 71.072 224.100 L 70 229.098 70 256.890 L 70 284.682 71.416 289.386 L 72.831 294.090 75.467 297.546 L 78.103 301.002 81.243 302.626 L 84.383 304.250 120.442 310.080 L 156.500 315.910 159.602 316.473 L 162.704 317.035 162.704 256.018 L 162.704 195 162.102 195.117 L 161.500 195.234 123.134 201.487 M 316 312.732 L 307.500 313.056 279.500 313.804 L 251.500 314.551 249 317.049 L 246.500 319.547 231.750 331.844 L 217 344.140 217 344.727 L 217 345.314 235.750 344.691 L 254.500 344.068 284 342.413 L 313.500 340.758 315.500 338.507 L 317.500 336.255 325.250 327.093 L 333 317.932 333 316.966 L 333 316 331 314 L 329 312 326.750 312.204 L 324.500 312.407 316 312.732 M 275 318.969 L 254.500 319.500 242.750 329.351 L 231 339.202 231 339.687 L 231 340.171 238.250 339.599 L 245.500 339.028 278 337.425 L 310.500 335.822 314.305 331.661 L 318.110 327.500 322.148 322.750 L 326.186 318 310.843 318.219 L 295.500 318.439 275 318.969 M 32.949 343.816 L 29 347.765 29 349.686 L 29 351.608 32.250 354.659 L 35.500 357.710 40 360.672 L 44.500 363.634 49.500 365.714 L 54.500 367.794 62 369.335 L 69.500 370.876 73.830 371.354 L 78.160 371.831 77.830 359.432 L 77.500 347.033 74.233 343.766 L 70.967 340.500 53.932 340.184 L 36.898 339.867 32.949 343.816 M 295.750 346.732 L 300 347.115 304.250 346.732 L 308.500 346.349 300 346.349 L 291.500 346.349 295.750 346.732 M 280.728 347.722 L 283.955 348.123 287.728 347.730 L 291.500 347.336 284.500 347.328 L 277.500 347.320 280.728 347.722 M 264.250 348.728 L 268 349.121 271.750 348.728 L 275.500 348.336 268 348.336 L 260.500 348.336 264.250 348.728 M 250.750 349.723 L 254 350.127 257.250 349.723 L 260.500 349.320 254 349.320 L 247.500 349.320 250.750 349.723 M 235.728 350.722 L 238.955 351.123 242.728 350.730 L 246.500 350.336 239.500 350.328 L 232.500 350.320 235.728 350.722 M 223.768 351.725 L 227.036 352.129 229.768 351.715 L 232.500 351.300 226.500 351.310 L 220.500 351.320 223.768 351.725 M 203.500 353.023 L 198.500 353.721 209 353.242 L 219.500 352.763 219.917 352.381 L 220.333 352 214.417 352.163 L 208.500 352.326 203.500 353.023 M 175.500 355.599 L 163.500 356.968 162 357.445 L 160.500 357.923 168 357.329 L 175.500 356.734 186.500 355.496 L 197.500 354.258 192.500 354.244 L 187.500 354.231 175.500 355.599 M 143.519 360.047 L 133.538 361.905 129.257 363.540 L 124.977 365.174 123.838 367.303 L 122.699 369.431 125.820 366.805 L 128.942 364.178 134.221 363 L 139.500 361.821 150 360.004 L 160.500 358.187 157 358.188 L 153.500 358.188 143.519 360.047";

export const downloadGPX = ({
  start,
  finish,
  histories,
  title,
}) => {
  let xmlText = [
    `<?xml version="1.0" encoding="utf-8"?>`,
    ` <gpx version="1.1" creator="Satify">`,
    `  <rte>`,
     `   <name>Trajetos</name>`,
    ` <extensions>`,
    `    <description>De ${start.format("DD/MM/YYYY HH:mm:ss")} até ${finish.format("DD/MM/YYYY HH:mm:ss")}</description>`,
    ` </extensions>`,
    ...histories.map(h => [
    `  <rtept lat="${h.props?.location?.lat}" lon="${h.props?.location?.lng}">`,
    `    <ele>0</ele>`,
    `  </rtept>`,
    ].join("\n")),
    `</gpx>`,
  ].join("\n")

  const byteCharacters = Buffer.from(xmlText, "utf-8");
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) { byteNumbers[i] = byteCharacters[i]; }
  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], { type: "application/xml" });

  const url = URL.createObjectURL(blob);;
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title}.gpx`;
  a.click();
  URL.revokeObjectURL(url);
}

export const downloadKML = ({
  start,
  finish,
  histories = [],
  title,
}) => {
  let xmlText = [
    `<?xml version="1.0" encoding="utf-8"?>`,
    `<kml xmlns="http://earth.google.com/kml/2.2">`,
	    `<Document>`,
		    `<name>Trajeto.kml</name>`,
        `<Style id="downArrowIcon">`,
          `<IconStyle>`,
            `<Icon>`,
              `<href>http://maps.google.com/mapfiles/kml/pal4/icon28.png</href>`,
            `</Icon>`,
          `</IconStyle>`,
        `</Style>`,
        `<StyleMap id="msn_ylw-pushpin11">`,
          `<Pair>`,
            `<key>normal</key>`,
            `<styleUrl>#sn_ylw-pushpin40</styleUrl>`,
          `</Pair>`,
          `<Pair>`,
            `<key>highlight</key>`,
            `<styleUrl>#sh_ylw-pushpin8</styleUrl>`,
          `</Pair>`,
        `</StyleMap>`,
        `<Style id="sh_ylw-pushpin8">`,
          `<IconStyle>`,
            `<scale>1.3</scale>`,
            `<Icon>`,
              `<href>http://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png</href>`,
            `</Icon>`,
            `<hotSpot x="20" y="2" xunits="pixels" yunits="pixels"/>`,
          `</IconStyle>`,
          `<LineStyle>`,
            `<color>ff00aaff</color>`,
            `<width>6</width>`,
          `</LineStyle>`,
        `</Style>`,
        `<Style id="sn_ylw-pushpin40">`,
          `<IconStyle>`,
            `<scale>1.1</scale>`,
            `<Icon>`,
              `<href>http://maps.google.com/mapfiles/kml/pushpin/ylw-pushpin.png</href>`,
            `</Icon>`,
            `<hotSpot x="20" y="2" xunits="pixels" yunits="pixels"/>`,
          `</IconStyle>`,
          `<LineStyle>`,
            `<color>ff00aaff</color>`,
            `<width>6</width>`,
          `</LineStyle>`,
        `</Style>`,
        `<Folder>`,
          `<open>0</open>`,
          `<name>Trajeto</name>`,
          ...(histories.map((h, i) => [
            `<Placemark>`,
              `<name>Ponto ${i+1}</name>`,
              `<styleUrl>#downArrowIcon</styleUrl>`,
              `<description>Velocidade: ${h.props.speed}km/h&lt;br&gt;${moment(h.datetime).format("DD/MM/YYYY HH:mm:ss")}</description>`,
              `<Point>`,
                `<coordinates>${h.props?.location?.lng},${h.props?.location?.lat}</coordinates>`,
              `</Point>`,
            `</Placemark>`,
          ].join("\n"))),
        `</Folder>`,
        `<Folder>`,
          `<Placemark>`,
            `<name>LINHA 001 - Naddime Derze/integração</name>`,
            `<styleUrl>#msn_ylw-pushpin11</styleUrl>`,
            `<LineString>`,
              `<tessellate>1</tessellate>`,
              `<coordinates>`,
                `${histories.map(h => `${h.props?.location?.lng},${h.props?.location?.lat}`).join("\n")}`,
              `</coordinates>`,
            `</LineString>`,
          `</Placemark>`,
        `</Folder>`,
	    `</Document>`,
    `</kml>`,
  ].join("\n");

  const byteCharacters = Buffer.from(xmlText, "utf-8");
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) { byteNumbers[i] = byteCharacters[i]; }
  const byteArray = new Uint8Array(byteNumbers);

  const blob = new Blob([byteArray], { type: "application/xml" });

  const url = URL.createObjectURL(blob);;
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title}.kml`;
  a.click();
  URL.revokeObjectURL(url);
}
